/**
 * Opt-in console tracing for the dashboard.
 *
 * The server has LOG_LEVEL; a browser has nothing, and a console that narrates
 * every upload by default is a console nobody reads. So this is off unless
 * asked for, in one of two ways:
 *
 *   localStorage.setItem('superchat:debug', 'true')   // persists
 *   ?debug=1                                          // this page load only
 *
 * On a dev server it defaults to on, because that is where someone is already
 * watching. Turn it off with `superchat:debug` set to 'false'.
 *
 * Never log a presigned URL: it is a bearer token for the object it names, and
 * a console someone screenshots into a bug report is not the place for one.
 */

const KEY = 'superchat:debug';

function readSetting() {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored !== null) return stored === 'true';
    if (new URLSearchParams(window.location.search).get('debug') === '1') return true;
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

let enabled = typeof window === 'undefined' ? false : readSetting();

export const debugEnabled = () => enabled;

export function setDebugEnabled(on) {
  enabled = Boolean(on);
  try {
    localStorage.setItem(KEY, String(enabled));
  } catch {
    /* private mode — the flag lives for this page only */
  }
}

/** `debug('upload', 'signed', { key })` -> `[superchat:upload] signed { … }` */
export function debug(scope, message, data) {
  if (!enabled) return;
  if (data === undefined) console.debug(`[superchat:${scope}] ${message}`);
  else console.debug(`[superchat:${scope}] ${message}`, data);
}

/**
 * A scoped logger with a stopwatch, for anything that takes long enough to be
 * worth timing — which an upload always is.
 */
export function debugTimer(scope, message, data) {
  const startedAt = performance.now();
  debug(scope, message, data);
  return (endMessage, endData) =>
    debug(scope, endMessage, { ...endData, ms: Math.round(performance.now() - startedAt) });
}

// Reachable from the console so a bug report can be produced without a rebuild.
if (typeof window !== 'undefined') {
  window.superchatDebug = { enable: () => setDebugEnabled(true), disable: () => setDebugEnabled(false) };
}

export default { debug, debugTimer, debugEnabled, setDebugEnabled };
